import React, { useEffect, useState } from "react";
import { ChevronRight } from "@mui/icons-material";
import { Box } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { Table, TableBody, TableCell, TableHead, TableRow } from "ui";

import { selectVersionsByProjectId, useGetVersionBomQuery, useGetVersionQuery } from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { useCanICollaboration } from "fond/hooks/useCanICollaboration";
import { useGetScenarioConfiguration } from "fond/hooks/useGetScenarioConfiguration";
import BomComparisonModal from "fond/project/bom/BomComparisonModal";
import BomModal from "fond/project/bom/BomModal";
import { load } from "fond/project/bom/redux";
import { Project } from "fond/types";
import { formatCurrency } from "fond/utils/currency";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { isCollaborationProject } from "fond/utils/project";
import { LinkButton } from "fond/widgets/Link/LinkButton";

import { BomCarvePanel } from "./BomCarvePanel";
import { ScenarioPanelBomComparisonRow } from "./ScenarioPanelBomComparisonRow";

type ScenarioPanelBomSectionProps = {
  project: Project;
};

export const ScenarioPanelBomSection: React.FC<ScenarioPanelBomSectionProps> = ({ project }) => {
  const dispatch = useAppDispatch();
  const { canCarveBOM, isLoading: isLoadingCollaboration, isDisabled } = useCanICollaboration();
  const versionId = useAppSelector((state) => state.project.versionId);
  const { configuration: scenarioConfiguration, isLoading: isLoadingScenarioConfiguration } = useGetScenarioConfiguration({ projectId: project.ID });
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const { value: isCompareBomsEnabled } = useFeatureFlag("compare_boms");
  const bom = useAppSelector((state) => state.bom.bom);
  const versions = useAppSelector((state) => selectVersionsByProjectId(state, project.ID));
  const baselineVersion = versions ? versions.at(-1) : null;
  const isBaseline = !!baselineVersion && baselineVersion.ID === versionId;
  const { data: baselineBom } = useGetVersionBomQuery(baselineVersion && !isBaseline ? baselineVersion.ID : skipToken);
  const [showBomModal, setShowBomModal] = useState(false);
  const [showBomComparisonModal, setShowBomComparisonModal] = useState(false);
  const architecture = version?.Architecture ?? null;

  useEffect(() => {
    dispatch(load(versionId));
  }, [versionId, dispatch]);

  const showBomModalClick = () => {
    setShowBomModal(true);
  };

  const showBomComparisonModalClick = () => {
    setShowBomComparisonModal(true);
  };

  if (!bom || isLoadingCollaboration || isLoadingScenarioConfiguration || !isCollaborationProject(project)) return null;

  return (
    <Box mt={2}>
      <Table variant="ag-grid-compact">
        <TableHead>
          <TableRow>
            <TableCell colSpan={2}>Bill of Materials</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Total estimated cost</TableCell>
            <TableCell align="right">
              <b>{formatCurrency(bom.total, { currency: scenarioConfiguration?.Currency })}</b>
            </TableCell>
          </TableRow>
          {!isBaseline && baselineVersion && baselineBom && (
            <ScenarioPanelBomComparisonRow
              baselineVersion={baselineVersion}
              currentTotal={bom.total}
              baselineTotal={baselineBom.total}
              currency={scenarioConfiguration?.Currency}
            />
          )}
          <TableRow hover onClick={showBomModalClick} sx={{ cursor: "pointer" }}>
            <TableCell colSpan={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <LinkButton
                  onClick={(e) => {
                    e.stopPropagation();
                    showBomModalClick();
                  }}
                >
                  View this scenario BOM
                </LinkButton>
                <ChevronRight fontSize="small" color="action" />
              </Box>
            </TableCell>
          </TableRow>
          {isCompareBomsEnabled && (
            <TableRow hover onClick={showBomComparisonModalClick} sx={{ cursor: "pointer" }}>
              <TableCell colSpan={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <LinkButton
                    onClick={(e) => {
                      e.stopPropagation();
                      showBomComparisonModalClick();
                    }}
                  >
                    View all scenarios BOMs
                  </LinkButton>
                  <ChevronRight fontSize="small" color="action" />
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {showBomModal && <BomModal project={project} architecture={architecture} onClose={() => setShowBomModal(false)} />}
      {showBomComparisonModal && (
        <BomComparisonModal project={project} architecture={architecture} onClose={() => setShowBomComparisonModal(false)} />
      )}
      {canCarveBOM && <BomCarvePanel versionId={versionId} isDisabled={isDisabled} />}
    </Box>
  );
};
